/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { computed, get } from '@ember/object';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import {
  filterBy,
  rejectBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import { sortTags } from '@linc-technologies/linced-common/utils/tags';

export default class PrintPost extends Component {
  MediaType = MediaType;

  // post is the post to be displayed.
  post = null;

  // includeAuthorImages specifies whether to show authors' profile images.
  includeAuthorImages = true;

  // includeLegalName specifies whether to include the students legal name.
  @tracked includeLegalName = false;

  // includeImages specifies whether to show images attached to Posts.
  includeImages = true;

  // includeOtherMedia specifies whether to list other media attached to Posts.
  includeOtherMedia = false;

  // includeComments specifies whether to show comments on Posts.
  includeComments = false;

  // post.page is an async belongsTo (a PromiseBelongsTo) and post.tags is
  // an async hasMany (a PromiseManyArray): native property access on
  // either asserts, so use classic get(). tagCategories itself is already
  // resolved to a plain array by the Page model; tags is not, so its
  // .content still needs resolving.
  @computed('post.page.tagCategories.[]')
  get tagCategoryIds() {
    return (get(this, 'post.page.tagCategories') ?? []).map(
      (category) => category.id,
    );
  }

  @computed('post.tags.content.[]', 'tagCategoryIds')
  get filteredTags() {
    let categoryIds = this.tagCategoryIds;
    return (get(this, 'post.tags.content') ?? []).filter((tag) => {
      return categoryIds.includes(tag.get('category.id'));
    });
  }

  @computed('post.media.content.@each.mediaType')
  get images() {
    // resolve post images before filtering.
    return filterBy(
      resolved(get(this, 'post.media')),
      'mediaType',
      MediaType.IMAGE,
    );
  }

  @computed('post.media.content.@each.mediaType')
  get attachments() {
    // resolve post attachments before filtering.
    return rejectBy(
      resolved(get(this, 'post.media')),
      'mediaType',
      MediaType.IMAGE,
    );
  }

  @computed('includeLegalName', 'post.author.{type,legalFullName,fullName}')
  get nameToDisplay() {
    let postAuthorType = this.post.author.get('type');
    if (this.includeLegalName && postAuthorType === PersonType.LEARNER) {
      return this.post.author.get('legalFullName');
    }
    return this.post.author.get('fullName');
  }

  @computed('filteredTags.[]', 'tagCategoryIds.[]')
  get sortedTags() {
    return sortTags(this.filteredTags, this.tagCategoryIds);
  }

  @computed('post.{tags.length,goals.length}')
  get hasTagsOrGoals() {
    return this.post.tags.length > 0 || this.post.goals.length > 0;
  }

  @computed('includeComments', 'post.comments.length')
  get showComments() {
    return this.includeComments && this.post.comments.length > 0;
  }
}
